/**
 * 1stbuddha — predictive-search.js
 *
 * <predictive-search> (snippets/predictive-search.liquid) inside the
 * #PredictiveSearch dialog:
 *   - typing (≥2 characters, 200 ms debounce) fetches
 *     sections/predictive-search.liquid through the Predictive Search API and
 *     swaps it into [data-predictive-results]; earlier requests are aborted
 *     and answers are cached per query
 *   - combobox keyboard pattern: ↑ ↓ move aria-activedescendant through the
 *     [role="option"] links, Enter opens the active one (or submits), Esc
 *     closes the dialog (dialog.js)
 *   - recent searches (last 5, localStorage, this browser only) are shown as
 *     chips while the field is empty
 *   - on desktop the panel starts under the announcement bar
 */
(() => {
  if (customElements.get('predictive-search')) return;

  const STORAGE_KEY = 'recent-searches';
  const readRecent = () => {
    try {
      const list = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(list) ? list.filter((item) => typeof item === 'string').slice(0, 5) : [];
    } catch {
      return [];
    }
  };
  const saveRecent = (term) => {
    const value = term.trim();
    if (value.length < 2) return;
    try {
      const list = [value, ...readRecent().filter((item) => item.toLowerCase() !== value.toLowerCase())].slice(0, 5);
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch {
      /* storage unavailable */
    }
  };

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cache = new Map();
      this.activeIndex = -1;
    }

    connectedCallback() {
      this.input = this.querySelector('[data-predictive-input]');
      this.results = this.querySelector('[data-predictive-results]');
      this.start = this.querySelector('[data-predictive-start]');
      this.status = this.querySelector('[data-predictive-status]');
      this.clearButton = this.querySelector('[data-predictive-clear]');
      this.form = this.querySelector('form');
      this.dialog = this.closest('dialog');
      if (!this.input || !this.results) return;

      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.clearButton?.addEventListener('click', () => {
        this.input.value = '';
        this.onInput();
        this.input.focus();
      });
      this.form?.addEventListener('submit', () => saveRecent(this.input.value));
      this.results.addEventListener('click', (event) => {
        if (event.target.closest('a')) saveRecent(this.input.value);
      });

      this.closest('dialog-component')?.addEventListener('dialog:open', () => {
        this.positionPanel();
        this.renderRecent();
        this.input.select();
      });
    }

    positionPanel() {
      if (!this.dialog) return;
      const bar = document.querySelector('.announcement-bar');
      const bottom = bar ? Math.max(0, bar.getBoundingClientRect().bottom) : 0;
      const rootSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
      this.dialog.style.setProperty('--search-panel-top', `${(bottom / rootSize).toFixed(2)}rem`);
    }

    renderRecent() {
      const group = this.querySelector('[data-recent]');
      const list = this.querySelector('[data-recent-list]');
      if (!group || !list) return;
      const recent = readRecent();
      list.replaceChildren(
        ...recent.map((term) => {
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.className = 'chip';
          link.href = `${this.form.action}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
          link.textContent = term;
          item.append(link);
          return item;
        })
      );
      group.hidden = recent.length === 0;
    }

    onInput() {
      const term = this.input.value.trim();
      if (this.clearButton) this.clearButton.hidden = term === '';
      clearTimeout(this.timer);
      if (term.length < 2) {
        this.controller?.abort();
        this.showStart(true);
        return;
      }
      this.timer = setTimeout(() => this.search(term), 200);
    }

    showStart(show) {
      if (this.start) this.start.hidden = !show;
      if (show) {
        this.results.replaceChildren();
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');
        this.activeIndex = -1;
      }
    }

    async search(term) {
      const key = term.toLowerCase();
      let html = this.cache.get(key);
      if (!html) {
        this.controller?.abort();
        this.controller = new AbortController();
        const params = new URLSearchParams({
          q: term,
          section_id: this.dataset.section,
          'resources[type]': 'query,product,collection,article,page',
          'resources[limit]': '4',
          'resources[limit_scope]': 'each',
          'resources[options][prefix]': 'last',
        });
        try {
          const response = await fetch(`${this.dataset.url}?${params}`, { signal: this.controller.signal });
          if (!response.ok) return;
          html = await response.text();
          this.cache.set(key, html);
        } catch {
          return;
        }
      }
      if (this.input.value.trim().toLowerCase() !== key) return;

      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.predictive');
      this.showStart(false);
      this.results.replaceChildren(...(fresh ? [fresh] : []));
      this.activeIndex = -1;
      this.input.setAttribute('aria-expanded', 'true');
      this.input.removeAttribute('aria-activedescendant');
      if (this.status) {
        this.status.textContent = fresh?.dataset.hasResults === 'true' ? this.results.querySelector('.predictive__all')?.textContent.trim() || '' : fresh?.textContent.trim() || '';
      }
    }

    options() {
      return [...this.results.querySelectorAll('[role="option"]')];
    }

    onKeydown(event) {
      // A search field swallows the first Esc (it clears itself); close the panel instead.
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closest('dialog-component')?.close();
        return;
      }
      const options = this.options();
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (!options.length) return;
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        this.activeIndex = (this.activeIndex + step + options.length) % options.length;
        options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.activeIndex)));
        const active = options[this.activeIndex];
        this.input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && this.activeIndex >= 0 && options[this.activeIndex]) {
        event.preventDefault();
        saveRecent(this.input.value);
        options[this.activeIndex].click();
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
